import { gql } from '@urql/core';
import { CollegeSocialsFragment } from 'generated/spin-graphql';
import React, { useCallback } from 'react';
import { ExternalLink } from '../common/ExternalLink';
import { FacebookIcon } from '../svg/socials/FacebookIcon';
import { InstagramIcon } from '../svg/socials/InstagramIcon';
import { TwitchIcon } from '../svg/socials/TwitchIcon';
import { TwitterIcon } from '../svg/socials/TwitterIcon';
import { WebsiteIcon } from '../svg/socials/WebsiteIcon';
import { YoutubeIcon } from '../svg/socials/YoutubeIcon';

gql`
    fragment CollegeSocials on espin_college {
        socials {
            facebook
            twitter
            instagram
            twitch_channel {
                id
                link_data
            }
            website
            youtube_channel {
                id
                link_data
            }
        }
    }
`;

export function CollegeSocialsDisplay({ socials }: CollegeSocialsFragment) {
    const getYTLink = useCallback((linkData: string) => {
        if (linkData.startsWith('c/') || linkData.startsWith('channel/') || linkData.startsWith('user/')) {
            return linkData;
        }
        if (linkData.startsWith('@')) {
            return linkData;
        }
        return `/results?search_query=${linkData}`;
    }, []);

    return (
        <div className="socials-container">
            {socials?.facebook && (
                <ExternalLink
                    className="social-icon"
                    ariaLabel={'Facebook'}
                    link={
                        socials.facebook.startsWith('http')
                            ? socials.facebook
                            : `https://facebook.com/${socials.facebook}`
                    }
                    icon={<FacebookIcon />}
                />
            )}
            {socials?.twitter && (
                <ExternalLink
                    className="social-icon"
                    ariaLabel={'Twitter'}
                    link={`https://www.twitter.com/${socials.twitter}`}
                    icon={<TwitterIcon />}
                />
            )}
            {socials?.instagram && (
                <ExternalLink
                    className="social-icon"
                    ariaLabel={'Instagram'}
                    link={
                        socials.instagram.startsWith('http') // https, etc
                            ? socials.instagram // just show it
                            : socials.instagram.startsWith('@') // @ user id
                            ? `https://www.instagram.com/${socials.instagram.split('@').pop()}` // pop the @ and show it
                            : `https://www.instagram.com/${socials.instagram}` // show at the end of insta
                    }
                    icon={<InstagramIcon />}
                />
            )}
            {socials?.youtube_channel && (
                <ExternalLink
                    className="social-icon"
                    ariaLabel={'YouTube'}
                    link={`https://www.youtube.com/${getYTLink(socials.youtube_channel.link_data)}`}
                    icon={<YoutubeIcon />}
                />
            )}
            {socials?.twitch_channel && (
                <ExternalLink
                    className="social-icon"
                    ariaLabel={'Twitch'}
                    link={`https://www.twitch.tv/${socials?.twitch_channel.link_data}`}
                    icon={<TwitchIcon />}
                />
            )}
            {socials?.website && (
                <ExternalLink
                    className="social-icon"
                    ariaLabel={'Website'}
                    link={`${socials.website.startsWith('http') ? socials.website : `https://${socials.website}`}`}
                    icon={<WebsiteIcon />}
                />
            )}
        </div>
    );
}
